import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import DOMPurify from 'dompurify';
import MsgReader from '@kenjiuno/msgreader';
import PostalMime from 'postal-mime';
import { attachmentsApi } from '../services/api';

/**
 * Determines how to preview a given attachment.
 * Returns a type string for supported formats, or null if unsupported.
 * Exported so TransactionDetail can use it for button state.
 */
export function getPreviewType(attachment) {
  if (!attachment) return null;
  const type = (attachment.fileType || '').toLowerCase();
  const ext = (attachment.fileName || '').split('.').pop().toLowerCase();

  if (type.includes('pdf') || ext === 'pdf') return 'pdf';
  if (
    type.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'tif', 'bmp', 'svg'].includes(ext)
  )
    return 'image';
  if (ext === 'docx' || type.includes('wordprocessingml')) return 'docx';
  if (
    ['xlsx', 'xls'].includes(ext) ||
    type.includes('spreadsheetml') ||
    type.includes('ms-excel')
  )
    return 'xlsx';
  if (ext === 'csv' || type === 'text/csv') return 'csv';
  if (
    ext === 'msg' ||
    type.includes('ms-outlook') ||
    type === 'application/vnd.ms-outlook'
  )
    return 'msg';
  if (ext === 'eml' || type === 'message/rfc822') return 'eml';
  if (ext === 'txt' || type === 'text/plain') return 'txt';
  if (['htm', 'html'].includes(ext) || type.includes('text/html')) return 'html';

  return null; // DOC (old binary), ZIP, DB, OXPS, etc.
}

// ─── Sub-renderers ────────────────────────────────────────────────────────────

function PdfPreview({ url, height = '72vh' }) {
  return (
    <iframe
      src={url}
      title="PDF Preview"
      style={{ width: '100%', height, border: 'none', display: 'block' }}
    />
  );
}

function ImagePreview({ url, fileName, height = '72vh' }) {
  return (
    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f0f0f0' }}>
      <img
        src={url}
        alt={fileName}
        style={{ maxWidth: '100%', maxHeight: height, objectFit: 'contain' }}
      />
    </Box>
  );
}

function SpreadsheetPreview({ html, height = '72vh' }) {
  return (
    <Box
      sx={{
        overflow: 'auto',
        maxHeight: height,
        p: 1,
        '& table': { borderCollapse: 'collapse', fontSize: '0.8rem', width: '100%' },
        '& td, & th': {
          border: '1px solid #e0e0e0',
          p: '4px 8px',
          whiteSpace: 'nowrap',
          verticalAlign: 'top',
        },
        '& tr:nth-of-type(even)': { backgroundColor: '#f5f5f5' },
        '& tr:first-of-type': { backgroundColor: '#32578b', color: '#fff', fontWeight: 'bold' },
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
}

function DocxPreview({ arrayBuffer, height = '72vh' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current && arrayBuffer) {
      renderAsync(arrayBuffer, containerRef.current, null, {
        className: 'docx-render',
        inWrapper: false,
        ignoreWidth: true,
      }).catch(console.error);
    }
  }, [arrayBuffer]);

  return (
    <Box
      ref={containerRef}
      sx={{
        p: 3,
        maxHeight: height,
        overflow: 'auto',
        '& .docx-render': { fontFamily: 'Georgia, serif', lineHeight: 1.6 },
      }}
    />
  );
}

function EmailPreview({ email }) {
  return (
    <Box>
      <Box sx={{ px: 3, py: 2, backgroundColor: '#f5f6f7', borderBottom: '1px solid #e0e0e0' }}>
        {email.subject && (
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {email.subject}
          </Typography>
        )}
        {email.from && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>From:</Box>
            {email.from}
          </Typography>
        )}
        {email.to && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>To:</Box>
            {email.to}
          </Typography>
        )}
        {email.date && (
          <Typography variant="body2" color="text.secondary">
            {email.date}
          </Typography>
        )}
      </Box>
      <Box sx={{ maxHeight: '56vh', overflow: 'auto' }}>
        {email.html ? (
          <iframe
            srcDoc={email.html}
            title="Email body"
            style={{ width: '100%', height: '56vh', border: 'none', display: 'block' }}
            sandbox=""
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {email.text || '(No message body)'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TextPreview({ text, isHtml, height = '72vh' }) {
  if (isHtml) {
    return (
      <iframe
        srcDoc={text}
        title="HTML Preview"
        style={{ width: '100%', height, border: 'none', display: 'block' }}
        sandbox="allow-same-origin"
      />
    );
  }
  return (
    <Box sx={{ p: 3, maxHeight: height, overflow: 'auto', backgroundColor: '#fafafa' }}>
      <Typography
        component="pre"
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// ─── Main Dialog ──────────────────────────────────────────────────────────────

function AttachmentPreviewDialog({ open, onClose, attachment, onDownload }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const previewHeight = isMobile ? '60vh' : '72vh';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);

  const previewType = getPreviewType(attachment);

  useEffect(() => {
    if (!open || !attachment) {
      setPreviewContent(null);
      setError(null);
      return;
    }
    loadPreview();
  }, [open, attachment?.id]); // eslint-disable-line

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewContent(null);
    try {
      const { url } = await attachmentsApi.getPreviewUrl(attachment.id);

      // PDF and images: pass the URL directly to the renderer (no download needed)
      if (previewType === 'pdf') {
        setPreviewContent({ type: 'pdf', url });
        return;
      }
      if (previewType === 'image') {
        setPreviewContent({ type: 'image', url });
        return;
      }

      // Everything else: download the file first
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch file (${response.status})`);

      if (previewType === 'txt') {
        const text = await response.text();
        setPreviewContent({ type: 'txt', text });
      } else if (previewType === 'html') {
        const text = await response.text();
        setPreviewContent({ type: 'html', text });
      } else if (previewType === 'csv') {
        const text = await response.text();
        const workbook = XLSX.read(text, { type: 'string' });
        const html = DOMPurify.sanitize(XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[0]]));
        setPreviewContent({ type: 'spreadsheet', html });
      } else {
        const arrayBuffer = await response.arrayBuffer();

        if (previewType === 'docx') {
          setPreviewContent({ type: 'docx', arrayBuffer });
        } else if (previewType === 'xlsx') {
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const html = DOMPurify.sanitize(XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[0]]));
          setPreviewContent({ type: 'spreadsheet', html });
        } else if (previewType === 'msg') {
          const reader = new MsgReader(arrayBuffer);
          const msg = reader.getFileData();
          setPreviewContent({
            type: 'email',
            email: {
              subject: msg.subject,
              from: msg.senderSmtpAddress || msg.senderName || '',
              to: (msg.recipients || [])
                .map((r) => r.smtpAddress || r.name)
                .filter(Boolean)
                .join(', '),
              date: msg.creationTime || '',
              html: msg.bodyHTML || null,
              text: msg.body || '',
            },
          });
        } else if (previewType === 'eml') {
          const parser = new PostalMime();
          const email = await parser.parse(arrayBuffer);
          const fmtAddr = (a) =>
            a ? [a.name, a.address ? `<${a.address}>` : ''].filter(Boolean).join(' ') : '';
          setPreviewContent({
            type: 'email',
            email: {
              subject: email.subject || '',
              from: fmtAddr(email.from),
              to: (email.to || []).map(fmtAddr).join(', '),
              date: email.date || '',
              html: email.html || null,
              text: email.text || '',
            },
          });
        }
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError('Could not load preview. Try downloading the file instead.');
    } finally {
      setLoading(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }
    if (!previewContent) return null;

    const { type } = previewContent;
    if (type === 'pdf') return <PdfPreview url={previewContent.url} height={previewHeight} />;
    if (type === 'image') return <ImagePreview url={previewContent.url} fileName={attachment.fileName} height={previewHeight} />;
    if (type === 'spreadsheet') return <SpreadsheetPreview html={previewContent.html} height={previewHeight} />;
    if (type === 'docx') return <DocxPreview arrayBuffer={previewContent.arrayBuffer} height={previewHeight} />;
    if (type === 'email') return <EmailPreview email={previewContent.email} />;
    if (type === 'txt') return <TextPreview text={previewContent.text} height={previewHeight} />;
    if (type === 'html') return <TextPreview text={previewContent.text} isHtml height={previewHeight} />;
    return null;
  };

  if (!attachment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          py: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: '85%' }}>
          {attachment.fileName}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, minHeight: 200 }}>{renderBody()}</DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() => onDownload(attachment)}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AttachmentPreviewDialog;
